// src/hr/portal/HrPortalApp.js -- the HR Portal, mounted at /hr-portal/*.
// Routes are relative: '' (People), people/:id, requests, whos-out, policies, checklists, reminders,
// reports, settings, year-end. GET /api/hr/me decides what renders: anyone who isn't HR is sent to the
// homepage before any HR data is requested. The server enforces every rule regardless.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, Route, Routes, useLocation, useResolvedPath } from 'react-router-dom';
import {
  FiAlertCircle, FiArrowLeft, FiBarChart2, FiBell, FiCalendar, FiCheckCircle, FiClipboard, FiFileText,
  FiSettings, FiTool, FiUsers
} from 'react-icons/fi';
import { hr } from '../api';
import { HrShell } from '../HrShell';
import useHrMe from '../useHrMe';
import { Btn, EmptyState, Skeleton, Toast, useToast } from '../kit';
import { todayNY } from '../timeoff/dates';
import { benefitYearRange, fullName } from './portalUtils';
import PeoplePage from './PeoplePage';
import PersonProfile from './PersonProfile';
import RequestsPage from './RequestsPage';
import WhosOutPage from './WhosOutPage';
import PoliciesAdmin from './PoliciesAdmin';
import ChecklistsPage from './ChecklistsPage';
import RemindersPage from './RemindersPage';
import ReportsPage from './ReportsPage';
import SettingsPage from './SettingsPage';
import YearEndPage from './YearEndPage';

const CRUMBS = {
  '': 'People',
  requests: 'Requests',
  'whos-out': "Who's Out",
  policies: 'Policies',
  checklists: 'Checklists',
  reminders: 'Reminders',
  reports: 'Reports',
  settings: 'Settings',
  'year-end': 'Year-end close-out'
};

// Loads something once `enabled` is true. reload() keeps the current data on screen until the new
// answer arrives (and keeps it when the reload fails) and resolves to { status, data }. Answers that
// arrive after unmount or after a newer load started are dropped.
function useResource(load, enabled) {
  const [state, setState] = useState({ status: 'idle', data: null });
  const latest = useRef(0);
  const mounted = useRef(true);
  const loadRef = useRef(load);
  loadRef.current = load;

  const reload = useCallback(async () => {
    latest.current += 1;
    const call = latest.current;
    setState((s) => ({ ...s, status: 'loading' }));
    let next;
    try {
      next = { status: 'ready', data: (await loadRef.current()) || null };
    } catch (err) {
      next = { status: 'error' };
    }
    if (mounted.current && call === latest.current) setState((s) => ({ ...s, ...next }));
    return next;
  }, []);

  useEffect(() => {
    mounted.current = true;
    if (enabled) reload();
    return () => { mounted.current = false; };
  }, [enabled, reload]);

  return { ...state, reload };
}

const loadPeople = () => hr.admin.people.list({ status: 'all' });
const loadContext = async () => {
  const [types, holidays, settings] = await Promise.all([
    hr.admin.types.list(), hr.admin.holidays.list(), hr.admin.settings.get()
  ]);
  return {
    types: (types && types.types) || [],
    holidays: (holidays && holidays.holidays) || [],
    settings: (settings && settings.settings) || null
  };
};

function LoadingPage() {
  return (
    <div className="hrp-page hrp-loading">
      <Skeleton label="Loading the HR Portal" width={200} height={26} />
      <Skeleton width={260} height={12} className="hrp-loading__sub" />
      <div className="hrp-pp-kpis">
        {[0, 1, 2, 3].map((i) => <Skeleton key={i} variant="block" height={96} />)}
      </div>
      <Skeleton variant="block" height={320} />
    </div>
  );
}

// today: optional 'YYYY-MM-DD' override (tests); defaults to today in New York, like the server.
export function HrPortalApp({ today: todayProp }) {
  const today = todayProp || todayNY();
  const { status, me, reload } = useHrMe();
  const [toast, showToast, dismissToast] = useToast();
  const location = useLocation();
  const base = useResolvedPath('.').pathname.replace(/\/+$/, '');

  const isHr = status === 'ready' && !!me && !!me.isHr;
  const people = useResource(loadPeople, isHr);
  const context = useResource(loadContext, isHr);
  const [requestCounts, setRequestCounts] = useState(null);

  useEffect(() => {
    if (!isHr) return undefined;
    let live = true;
    hr.admin.requests.list({ view: 'hr' })
      .then((body) => { if (live && body && body.counts) setRequestCounts((c) => c || body.counts); })
      .catch(() => {});
    return () => { live = false; };
  }, [isHr]);

  const ctx = context.data || { types: [], holidays: [], settings: null };
  const yearRange = useMemo(() => {
    if (me && me.yearRange && me.yearRange.start) return { year: me.year, ...me.yearRange };
    return benefitYearRange(today, ctx.settings && ctx.settings.benefitYearStart);
  }, [me, today, ctx.settings]);

  if (status === 'ready' && me && !me.isHr) return <Navigate to="/main-homepage" replace />;

  const page = location.pathname.slice(base.length).replace(/^\/+/, '').split('/')[0];
  let crumb = CRUMBS[page] || CRUMBS[''];
  if (page === 'people') {
    crumb = <Link to="."><FiArrowLeft aria-hidden="true" /> People</Link>;
  }

  const kpis = people.data && people.data.kpis;
  const sections = isHr ? [{
    title: 'HR',
    items: [
      { to: '.', label: 'People', icon: FiUsers, count: kpis ? kpis.active : undefined },
      { to: 'requests', label: 'Requests', icon: FiCheckCircle, badge: requestCounts ? requestCounts.hr || undefined : undefined },
      { to: 'whos-out', label: "Who's Out", icon: FiCalendar },
      { to: 'policies', label: 'Policies', icon: FiFileText },
      { to: 'checklists', label: 'Checklists', icon: FiClipboard },
      { to: 'reminders', label: 'Reminders', icon: FiBell, badge: kpis ? kpis.expiringSoon || undefined : undefined },
      { to: 'reports', label: 'Reports', icon: FiBarChart2 },
      { to: 'settings', label: 'Settings', icon: FiSettings }
    ]
  }] : [];

  const personBlock = isHr && me.employee
    ? { id: me.employee.id, name: fullName(me.employee), sub: me.isOwner ? 'HR · Owner' : 'HR' }
    : null;

  let body;
  if (status === 'loading') {
    body = <LoadingPage />;
  } else if (status === 'unavailable') {
    body = (
      <EmptyState icon={FiTool} title="HR Portal is being set up">
        People, requests and records will appear here once it's ready. Check back soon.
      </EmptyState>
    );
  } else if (!isHr) {
    body = (
      <EmptyState icon={FiAlertCircle} title="We couldn't open the HR Portal" action={<Btn variant="ghost" onClick={reload}>Try again</Btn>}>
        Something went wrong on our side. Try again in a moment.
      </EmptyState>
    );
  } else {
    const shared = {
      me,
      today,
      people,
      types: ctx.types,
      holidays: ctx.holidays,
      settings: ctx.settings,
      context,
      yearRange,
      onToast: showToast
    };
    body = (
      <Routes>
        <Route index element={<PeoplePage {...shared} />} />
        <Route path="people/:id" element={<PersonProfile {...shared} />} />
        <Route path="requests" element={<RequestsPage {...shared} onCounts={setRequestCounts} />} />
        <Route path="whos-out" element={<WhosOutPage {...shared} />} />
        <Route path="policies" element={<PoliciesAdmin {...shared} />} />
        <Route path="checklists" element={<ChecklistsPage {...shared} />} />
        <Route path="reminders" element={<RemindersPage {...shared} />} />
        <Route path="reports" element={<ReportsPage {...shared} />} />
        <Route path="settings" element={<SettingsPage {...shared} />} />
        <Route path="year-end" element={<YearEndPage {...shared} />} />
        <Route path="*" element={<Navigate to=".." replace />} />
      </Routes>
    );
  }

  return (
    <HrShell brand="HR Portal" brandLetter="H" sections={sections} person={personBlock} crumb={crumb}>
      {body}
      <Toast toast={toast} onDismiss={dismissToast} />
    </HrShell>
  );
}

export default HrPortalApp;
